import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  LinearProgress, 
  Button, 
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Grid,
  Fab,
  Tooltip,
  InputAdornment,
  Alert,
  IconButton,
  MobileStepper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Collapse,
  ListItemButton
} from '@mui/material';
import { Add as AddIcon, Backspace as BackspaceIcon, MoreVert as MoreVertIcon, ExpandLess, ExpandMore } from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format, getDaysInMonth, subDays, startOfDay, endOfDay, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { useUser } from '../context/UserContext';
import { useTransactions } from '../context/TransactionContext';
import { formatCurrency } from '../utils/formatters';
import moment from 'moment-timezone';
import confetti from 'canvas-confetti';
import axiosInstance from '../utils/axiosConfig';
import { fetchWeeklySpendingData } from '../utils/weeklyFetchUtil';
import { fetchMonthlySpendingData } from '../utils/monthlyFetchUtil';

// Default categories
const CATEGORIES = [
  'Coffee', 
  'Lunch', 
  'Dinner', 
  'Food', 
  'Groceries', 
  'Gas', 
  'Snacks', 
  'Clothes', 
  'Entertainment', 
  'Transportation', 
  'Utilities', 
  'Other'
];

const formatNumber = (num) => {
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    style: 'currency',
    currency: 'USD'
  }).replace('.00', '');
};

const HomePage = () => {
  const { profile } = useUser();
  const { 
    dailyBudget, 
    fetchDailyBudget, 
    transactions,
    fetchTransactions,
    createTransaction, 
    loading, 
    error, 
    setError,
    setTransactions 
  } = useTransactions();
  
  const [openDialog, setOpenDialog] = useState(false);
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [customCategory, setCustomCategory] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [note, setNote] = useState('');
  const [formError, setFormError] = useState('');
  
  // Add state for numeric keypad input
  const [numericInput, setNumericInput] = useState('');
  // Frequently used categories (default to common ones in case we don't have transaction data)
  const [quickCategories, setQuickCategories] = useState(['Food', 'Coffee', 'Gas', 'Groceries', 'Lunch', 'Dinner']);
  
  // States for spending chart
  const [showSpendingCard, setShowSpendingCard] = useState(true);
  const [isPinned, setIsPinned] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  const [isDailyCompact, setIsDailyCompact] = useState(false);
  const [isWeeklyCompact, setIsWeeklyCompact] = useState(false);
  const [isMonthlyCompact, setIsMonthlyCompact] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [weeklyMenuOpen, setWeeklyMenuOpen] = useState(false);
  const [monthlyMenuOpen, setMonthlyMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const weeklyMenuRef = useRef(null);
  const monthlyMenuRef = useRef(null);
  
  // Add state for weekly spending chart
  const [showWeeklyCard, setShowWeeklyCard] = useState(true);
  
  // Add state for weekly data
  const [weeklyData, setWeeklyData] = useState({ amounts: [], labels: [] });
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  
  // Add state for monthly spending chart
  const [showMonthlyCard, setShowMonthlyCard] = useState(true);
  const [monthlyData, setMonthlyData] = useState({ amounts: [], labels: [] });
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  
  // Add state for current week's total spending
  const [currentWeekTotal, setCurrentWeekTotal] = useState(0);
  
  // Add state for Swipeable Charts
  const [activeStep, setActiveStep] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const maxSteps = 4; // Daily, Weekly, Monthly, Progress
  
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(null);
  const [weeklyTransactions, setWeeklyTransactions] = useState(Array(4).fill([]));
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Add state for daily and monthly breakdowns
  const [dailyBreakdownOpen, setDailyBreakdownOpen] = useState(false);
  const [monthlyBreakdownOpen, setMonthlyBreakdownOpen] = useState(false);
  const [dailyTransactions, setDailyTransactions] = useState([]);
  const [monthlyTransactions, setMonthlyTransactions] = useState([]);
  
  // Add state for monthly transactions
  const [monthlyTransactionsForCumulative, setMonthlyTransactionsForCumulative] = useState([]);
  
  // Fetch daily budget on component mount
  useEffect(() => {
    fetchDailyBudget();
    fetchTransactions();
  }, [fetchDailyBudget, fetchTransactions]);
  
  // Determine frequently used categories from transactions
  useEffect(() => {
    if (transactions && transactions.length > 0) {
      // Count category occurrences
      const categoryCounts = {};
      transactions.forEach(transaction => {
        const cat = transaction.category || 'Uncategorized';
        categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
      });
      
      // Sort by count (descending)
      const sortedCategories = Object.keys(categoryCounts).sort((a, b) => 
        categoryCounts[b] - categoryCounts[a]
      );
      
      // Get top categories (at least 4, up to 6)
      const topCategories = sortedCategories.slice(0, Math.min(6, sortedCategories.length));
      // Ensure we have at least 4 categories
      if (topCategories.length < 4) {
        // Add default categories that aren't already in the list
        const defaultCategories = ['Food', 'Coffee', 'Gas', 'Groceries'];
        for (const cat of defaultCategories) {
          if (!topCategories.includes(cat) && topCategories.length < 4) {
            topCategories.push(cat);
          }
        }
      }
      setQuickCategories(topCategories);
    }
  }, [transactions]);
  
  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
      if (weeklyMenuRef.current && !weeklyMenuRef.current.contains(event.target)) {
        setWeeklyMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuRef, weeklyMenuRef]);
  
  // Handle numeric input
  const handleNumericInput = (value) => {
    if (value === 'backspace') {
      setNumericInput(prev => prev.slice(0, -1));
      return;
    }
    
    if (value === '.') {
      // Don't add another decimal point if one already exists
      if (numericInput.includes('.')) return;
      
      // If numericInput is empty, add a "0" before the decimal point
      if (numericInput === '') {
        setNumericInput('0.');
        return;
      }
    }
    
    setNumericInput(prev => prev + value);
    // Also update the regular amount field for consistency
    setAmount(prev => {
      const newValue = value === 'backspace' ? 
                       numericInput.slice(0, -1) : 
                       (value === '.' && numericInput === '') ? 
                       '0.' : numericInput + value;
      return newValue;
    });
  };
  
  // Handle quick category selection
  const handleQuickCategory = (selectedCategory) => {
    if (numericInput === '') return;
    setCategory(selectedCategory);
  };
  
  const handleOpenDialog = () => {
    // Reset form fields
    setAmount('');
    setCategory('');
    setCustomCategory('');
    setSelectedDate(new Date());
    setNote('');
    setFormError('');
    setNumericInput('');
    
    setOpenDialog(true);
  };
  
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  
  // Handle spending chart controls
  const handlePin = () => {
    setIsPinned(!isPinned);
    setMenuOpen(false);
  };

  const handleResize = () => {
    setIsCompact(!isCompact);
    setMenuOpen(false);
  };

  const handleRemove = () => {
    setShowSpendingCard(false);
    setMenuOpen(false);
  };
  
  const handleSubmit = async () => {
    setFormError('');
    
    // Form validation
    let amountValue;
    
    // Prefer numericInput if it exists, otherwise use amount
    if (numericInput) {
      amountValue = parseFloat(numericInput);
    } else if (amount.trim()) {
      amountValue = parseFloat(amount);
    } else {
      setFormError('Please enter an amount');
      return;
    }
    
    if (isNaN(amountValue) || amountValue <= 0) {
      setFormError('Please enter a valid amount greater than 0');
      return;
    }
    
    // Determine the final category value
    let finalCategory = 'Uncategorized';
    
    if (category === 'custom' && customCategory.trim()) {
      finalCategory = customCategory.trim();
    } else if (category) {
      finalCategory = category;
    }
    
    try {
      console.log(`Submitting transaction with date: ${moment(selectedDate).format()}`);
      
      await createTransaction({
        amount: amountValue,
        category: finalCategory,
        date: selectedDate,
        note: note.trim()
      });
      
      // Refresh budget data after transaction creation
      await fetchDailyBudget();
      // Also refresh transactions to update frequent categories
      await fetchTransactions();
      
      // Close the dialog
      handleCloseDialog();
      
      // Trigger confetti effect after successful transaction
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#1976d2', '#4caf50', '#ff9800']  // Blue, green, orange colors
      });
      
    } catch (error) {
      setFormError('Failed to save transaction: ' + error.message);
    }
  };
  
  // Calculate monthly pro-rata spending limit
  const calculateMonthlyProRataLimit = (budget) => {
    if (!budget || !budget.monthlyLimit) return 0;
    
    const today = new Date();
    const currentDay = today.getDate();
    const totalDaysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const remainingDays = totalDaysInMonth - currentDay + 1; // Include today
    
    // c. monthly pro rata spending limit = monthly spending limit * (days remaining in month/total days in month)
    return budget.monthlyLimit * (remainingDays / totalDaysInMonth);
  };

  // Calculate remaining spending in the month
  const calculateRemainingMonthlySpending = (budget) => {
    if (!budget) return 0;
    
    // d. Remaining spending in the month = min(Monthly spending limit, monthly pro rata spending limit) - monthly $ spent already
    const monthlyLimit = budget.monthlyLimit || 0;
    const spentSoFar = budget.totalMonthlySpending || 0;
    
    // Allow negative values to accurately show overspending
    return monthlyLimit - spentSoFar;
  };

  // Calculate adjusted daily goal
  const calculateAdjustedDailyGoal = (budget) => {
    if (!budget || !budget.remainingDays || budget.remainingDays <= 0) return 0;
    
    // a. Daily goal = Remaining spending for the month / number of remaining days in the month
    const remainingSpending = calculateRemainingMonthlySpending(budget);
    
    // If we've already overspent for the month, daily goal should be very low (but not zero)
    if (remainingSpending <= 0) return 0.01;
    
    return remainingSpending / budget.remainingDays;
  };
  
  // Calculate progress percentage for daily goal
  const calculateProgress = () => {
    if (!dailyBudget) return 0;
    
    const { spentToday } = dailyBudget;
    const adjustedDailyGoal = calculateAdjustedDailyGoal(dailyBudget);
    
    if (adjustedDailyGoal <= 0) return 100;
    
    return Math.min(100, (spentToday / adjustedDailyGoal) * 100);
  };
  
  // Determine progress bar color based on spending
  const getProgressColor = () => {
    const progress = calculateProgress();
    
    if (progress < 60) return 'success';
    if (progress < 80) return 'warning';
    if (progress < 100) return 'error';
    return 'error';
  };
  
  // Calculate monthly progress
  const calculateMonthlyProgress = () => {
    if (!dailyBudget) return 0;
    
    const { totalMonthlySpending, monthlyLimit } = dailyBudget;
    
    if (monthlyLimit <= 0) return 100;
    
    return Math.min(100, (totalMonthlySpending / monthlyLimit) * 100);
  };
  
  // Get monthly progress color
  const getMonthlyProgressColor = () => {
    const progress = calculateMonthlyProgress();
    
    if (progress < 60) return 'success';
    if (progress < 80) return 'warning';
    if (progress < 100) return 'error';
    return 'error';
  };
  
  // Refresh budget data when dialog closes
  useEffect(() => {
    if (openDialog === false) {
      fetchDailyBudget();
    }
  }, [openDialog, fetchDailyBudget]);

  // Prepare data for the chart
  const today = new Date();
  
  // Mock data if API data isn't available
  const mockData = {
    daily: {
      spent: dailyBudget?.spentToday || 0,
      target: dailyBudget?.initialDailyGoal || 100, // Use initialDailyGoal instead of dailyGoal
      remaining: 0 // Default to 0 instead of 72
    },
    weekly: {
      spent: dailyBudget?.totalMonthlySpending ? dailyBudget.totalMonthlySpending / 4 : 0,
      remaining: 0,
      target: dailyBudget?.monthlyLimit ? dailyBudget.monthlyLimit / 4 : 700
    },
    monthly: {
      spent: dailyBudget?.totalMonthlySpending || 0,
      remaining: 0,
      target: dailyBudget?.monthlyLimit || 3000
    }
  };
  
  const daysInMonth = getDaysInMonth(today);
  const currentDay = today.getDate();
  const daysLeft = daysInMonth - currentDay;
  // Use initialDailyGoal for the target line in the chart, which doesn't adjust when overspent
  const dailyTarget = dailyBudget?.initialDailyGoal || (mockData.monthly.target / daysInMonth);
  
  // Get last 7 days for chart
  const getRecentDailySpending = () => {
    // Create an array for the last 7 days with 0 as initial values
    const last7Days = Array(7).fill(0);
    const dayLabels = [];
    
    // Generate dates for the last 7 days
    for (let i = 6; i >= 0; i--) {
      const date = subDays(today, i);
      const formattedDate = format(date, 'yyyy-MM-dd');
      dayLabels.push(format(date, 'EEE')); // Day name, e.g., 'Mon'
      
      // For the daily chart, we may need to fetch data from the previous month
      // Use both regular transactions and monthlyTransactionsForCumulative
      if (Array.isArray(transactions) && transactions.length > 0) {
        transactions.forEach(transaction => {
          if (!transaction?.date) return;
          
          try {
            // Handle different date formats
            const transactionDate = new Date(transaction.date);
            if (isNaN(transactionDate.getTime())) return; // Invalid date
            
            const transactionDateFormatted = format(transactionDate, 'yyyy-MM-dd');
            if (formattedDate === transactionDateFormatted) {
              last7Days[6-i] += transaction.amount || 0;
            }
          } catch (err) {
            console.error('Error parsing transaction date:', err);
          }
        });
      }
      
      // Also check monthlyTransactionsForCumulative for current month data
      if (monthlyTransactionsForCumulative && Array.isArray(monthlyTransactionsForCumulative) && monthlyTransactionsForCumulative.length > 0) {
        monthlyTransactionsForCumulative.forEach(transaction => {
          if (!transaction?.date) return;
          
          try {
            // Handle different date formats
            const transactionDate = new Date(transaction.date);
            if (isNaN(transactionDate.getTime())) return; // Invalid date
            
            const transactionDateFormatted = format(transactionDate, 'yyyy-MM-dd');
            if (formattedDate === transactionDateFormatted) {
              // Check if we've already counted this transaction in the regular transactions
              const isAlreadyCounted = transactions?.some(t => {
                if (!t?.date) return false;
                const tDate = new Date(t.date);
                return (
                  tDate.getTime() === transactionDate.getTime() && 
                  t.amount === transaction.amount
                );
              });
              
              if (!isAlreadyCounted) {
                last7Days[6-i] += transaction.amount || 0;
              }
            }
          } catch (err) {
            console.error('Error parsing transaction date:', err);
          }
        });
      }
    }
    
    return {
      amounts: last7Days,
      labels: dayLabels
    };
  };
  
  const historicalData = getRecentDailySpending();
  
  // Find maximum amount for scaling the chart properly
  const maxAmount = Math.max(...historicalData.amounts, dailyTarget);
  // Scale factor - use 1.2x the max amount to leave some space at top
  const chartMaxValue = Math.max(maxAmount * 1.2, 50); // Use at least 50 as minimum scale
  
  // Update fetchWeeklySpending to store transactions
  const fetchWeeklySpending = useCallback(async () => {
    try {
      setWeeklyLoading(true);
      const weeklySpendingData = await fetchWeeklySpendingData();
      
      // Set the weekly chart data with default empty arrays
      setWeeklyData({
        amounts: weeklySpendingData?.amounts || [],
        labels: weeklySpendingData?.labels || []
      });
      
      // Store transactions with default empty array
      setWeeklyTransactions(weeklySpendingData?.transactions || Array(4).fill([]));
      
      // Get the most recent week's total (last item in the amounts array)
      const amounts = weeklySpendingData?.amounts || [];
      const mostRecentWeekTotal = amounts[amounts.length - 1] || 0;
      setCurrentWeekTotal(mostRecentWeekTotal);
    } catch (err) {
      console.error("Error in HomePage fetchWeeklySpending:", err);
      // Fallback to mock data
      setWeeklyData({
        amounts: [520, 485, 630, 540],
        labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4']
      });
      setWeeklyTransactions(Array(4).fill([]));
      setCurrentWeekTotal(540);
    } finally {
      setWeeklyLoading(false);
    }
  }, []);

  // Function to fetch monthly spending data
  const fetchMonthlySpending = useCallback(async () => {
    try {
      setMonthlyLoading(true);
      const monthlySpendingData = await fetchMonthlySpendingData();
      setMonthlyData({
        amounts: monthlySpendingData?.amounts || [],
        labels: monthlySpendingData?.labels || []
      });
    } catch (err) {
      console.error("Error in HomePage fetchMonthlySpending:", err);
      // Fallback to mock data
      setMonthlyData({
        amounts: [1250, 1475, 1380],
        labels: ['Month 1', 'Month 2', 'Month 3']
      });
    } finally {
      setMonthlyLoading(false);
    }
  }, []);

  // Call this function on mount and when transactions change
  useEffect(() => {
    fetchDailyBudget();
    fetchTransactions();
    fetchWeeklySpending();
    fetchMonthlySpending();
    
    // Check for day change to reset daily spending
    const checkForDayChange = () => {
      const lastViewDate = localStorage.getItem('lastViewDate');
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
      
      if (lastViewDate !== today) {
        console.log('Day changed, refreshing budget data');
        localStorage.setItem('lastViewDate', today);
        fetchDailyBudget(); // Re-fetch to get reset daily spending
      }
    };
    
    checkForDayChange();
  }, [fetchDailyBudget, fetchTransactions, fetchWeeklySpending, fetchMonthlySpending]);

  // Also refresh weekly and monthly data when a transaction is created/updated
  useEffect(() => {
    if (!openDialog) {
      fetchWeeklySpending();
      fetchMonthlySpending();
    }
  }, [openDialog, fetchWeeklySpending, fetchMonthlySpending]);
  
  // Handle Remove for Weekly Chart
  const handleRemoveWeekly = () => {
    setShowWeeklyCard(false);
    setWeeklyMenuOpen(false);
  };
  
  // Calculate the original weekly target (monthly limit divided by 4)
  const originalWeeklyTarget = dailyBudget?.monthlyLimit ? dailyBudget.monthlyLimit / 4 : 700;

  // Modify the weekly target calculation to use remaining budget (for the remaining amount display)
  const calculateWeeklyTarget = () => {
    if (!dailyBudget) return 700;
    
    const { monthlyLimit, totalMonthlySpending, remainingDays } = dailyBudget;
    if (!monthlyLimit) return 700;
    
    const remainingBudget = Math.max(0, monthlyLimit - totalMonthlySpending);
    
    // Calculate weeks remaining in month (approximately)
    const weeksRemaining = Math.max(1, Math.ceil(remainingDays / 7));
    
    // If we've overspent, weekly target should be very low (but not zero)
    if (remainingBudget <= 0) return 0.01;
    
    // Weekly target is the remaining budget divided by weeks remaining
    return remainingBudget / weeksRemaining;
  };

  const weeklyTarget = calculateWeeklyTarget();

  // Find maximum amount for scaling the weekly chart properly
  // Use originalWeeklyTarget for the chart scaling
  const weeklyMaxAmount = Math.max(...weeklyData.amounts, originalWeeklyTarget);
  // Scale factor - use 1.2x the max amount to leave some space at top
  const weeklyChartMaxValue = Math.max(weeklyMaxAmount * 1.2, 200);
  
  // Handle Remove for Monthly Chart
  const handleRemoveMonthly = () => {
    setShowMonthlyCard(false);
    setMonthlyMenuOpen(false);
  };
  
  // Calculate monthly target from budget
  const monthlyTarget = dailyBudget?.monthlyLimit || 1500;

  // Find maximum amount for scaling the monthly chart
  const monthlyMaxAmount = Math.max(...monthlyData.amounts, monthlyTarget);
  // Scale factor - use 1.2x the max amount to leave some space at top
  const monthlyChartMaxValue = Math.max(monthlyMaxAmount * 1.2, 300);
  
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      setActiveStep((prev) => (prev + 1) % maxSteps);
    }
    if (isRightSwipe) {
      setActiveStep((prev) => (prev - 1 + maxSteps) % maxSteps);
    }
    
    setTouchStart(null);
    setTouchEnd(null);
  };
  
  // Add helper function to calculate totals
  const calculateTotal = (transactions) => {
    return transactions?.reduce((sum, t) => sum + (t.amount || 0), 0) || 0;
  };

  // Update getCategoryBreakdown to include transaction count
  const getCategoryBreakdown = (transactions = []) => {
    if (!transactions) return [];
    
    const categoryMap = {};
    let total = 0;
    
    transactions.forEach(transaction => {
      const category = transaction.category || 'Uncategorized';
      const amount = transaction.amount || 0;
      
      if (!categoryMap[category]) {
        categoryMap[category] = {
          amount: 0,
          count: 0
        };
      }
      
      categoryMap[category].amount += amount;
      categoryMap[category].count += 1;
      total += amount;
    });
    
    // Convert to array and sort by amount
    return Object.entries(categoryMap)
      .map(([category, data]) => ({
        category,
        amount: data.amount,
        count: data.count,
        percentage: total > 0 ? (data.amount / total * 100).toFixed(1) : 0
      }))
      .sort((a, b) => b.amount - a.amount);
  };

  // Get transactions for a specific category in a week
  const getCategoryTransactions = (transactions = [], category) => {
    if (!transactions) return [];
    
    return transactions.filter(t => t.category === category)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  // Handle week box click
  const handleWeekClick = (index) => {
    setSelectedWeekIndex(index);
    setBreakdownOpen(true);
  };
  
  // Handle category click in breakdown
  const handleCategoryClick = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Format transaction date
  const formatTransactionDate = (dateString) => {
    return format(new Date(dateString), 'MMM d, h:mm a');
  };
  
  // Add new functions for handling bar clicks
  const handleDailyBarClick = async (date) => {
    try {
      const start = startOfDay(date);
      const end = endOfDay(date);
      const response = await axiosInstance.get('/transactions', {
        params: {
          startDate: start.toISOString(),
          endDate: end.toISOString(),
          limit: 1000,
          sortBy: 'date',
          sortDirection: 'desc'
        }
      });
      setDailyTransactions(response.data.transactions || []);
      setDailyBreakdownOpen(true);
    } catch (error) {
      console.error('Error fetching daily transactions:', error);
      setDailyTransactions([]);
    }
  };

  const handleMonthlyBarClick = async (monthIndex) => {
    try {
      const today = new Date();
      const targetMonth = subMonths(today, 2 - monthIndex); // Convert index to proper month
      const start = startOfMonth(targetMonth);
      const end = endOfMonth(targetMonth);
      const response = await axiosInstance.get('/transactions', {
        params: {
          startDate: start.toISOString(),
          endDate: end.toISOString(),
          limit: 1000,
          sortBy: 'date',
          sortDirection: 'desc'
        }
      });
      setMonthlyTransactions(response.data.transactions || []);
      setMonthlyBreakdownOpen(true);
    } catch (error) {
      console.error('Error fetching monthly transactions:', error);
      setMonthlyTransactions([]);
    }
  };
  
  // Add new useEffect to fetch enough historical transactions
  useEffect(() => {
    const fetchHistoricalTransactions = async () => {
      try {
        // Get start date (use beginning of previous month to ensure full coverage)
        const today = new Date();
        const previousMonth = new Date(today);
        previousMonth.setMonth(today.getMonth() - 1);
        const startDate = startOfMonth(previousMonth);

        const endDate = new Date();
        endDate.setHours(23, 59, 59, 999);

        const response = await axiosInstance.get('/transactions', {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            limit: 1000,
            sortBy: 'date',
            sortDirection: 'asc'
          }
        });

        // Store the historical transactions
        setTransactions(response.data.transactions || []);
      } catch (error) {
        console.error('Error fetching historical transactions:', error);
      }
    };

    fetchHistoricalTransactions();
  }, []);

  // Add useEffect to fetch monthly transactions
  useEffect(() => {
    const fetchMonthlyTransactionsForCumulative = async () => {
      try {
        const today = new Date();
        // Instead of using monthStart, fetch from the beginning of the month to ensure complete data
        const monthStart = startOfMonth(today);
        const monthEnd = endOfMonth(today);

        const response = await axiosInstance.get('/transactions', {
          params: {
            startDate: monthStart.toISOString(),
            endDate: monthEnd.toISOString(),
            limit: 1000,
            sortBy: 'date',
            sortDirection: 'asc'
          }
        });

        setMonthlyTransactionsForCumulative(response.data.transactions || []);
      } catch (error) {
        console.error('Error fetching monthly transactions:', error);
        setMonthlyTransactionsForCumulative([]);
      }
    };

    fetchMonthlyTransactionsForCumulative();
  }, [openDialog]); // Add openDialog as dependency to refresh after new transaction

  // Update getCumulativeSpendingData function
  const getCumulativeSpendingData = () => {
    const today = new Date();
    const daysInMonth = getDaysInMonth(today);
    
    // Calculate daily target spending (linear progression)
    const monthlyTarget = dailyBudget?.monthlyLimit || 3000;
    const dailyTargetAmount = monthlyTarget / daysInMonth;
    
    // Create arrays for target and actual spending
    const targetSpending = Array(daysInMonth).fill(0);
    const actualSpending = Array(daysInMonth).fill(0);
    
    // Fill target spending (linear progression)
    for (let i = 0; i < daysInMonth; i++) {
      targetSpending[i] = dailyTargetAmount * (i + 1);
    }

    // Calculate actual cumulative spending using monthly transactions
    let monthlySpentTotal = 0;
    if (monthlyTransactionsForCumulative && monthlyTransactionsForCumulative.length > 0) {
      // Create a Map to store daily totals
      const dailyTotals = new Map();
      
      // Process each transaction and add to daily totals
      monthlyTransactionsForCumulative.forEach(transaction => {
        if (!transaction?.date) return;
        
        const transactionDate = new Date(transaction.date);
        const dayIndex = transactionDate.getDate() - 1;
        
        if (dayIndex >= 0 && dayIndex < daysInMonth) {
          // Get current total for this day or 0 if none exists
          const currentTotal = dailyTotals.get(dayIndex) || 0;
          // Add this transaction amount to the day's total
          dailyTotals.set(dayIndex, currentTotal + (transaction.amount || 0));
        }
      });
      
      // Calculate cumulative spending
      let runningTotal = 0;
      for (let i = 0; i < daysInMonth; i++) {
        runningTotal += dailyTotals.get(i) || 0;
        actualSpending[i] = runningTotal;
      }
      
      // For days in the future (after today), use today's total
      const todayIndex = today.getDate() - 1;
      if (todayIndex >= 0 && todayIndex < daysInMonth) {
        const todayTotal = actualSpending[todayIndex];
        for (let i = todayIndex + 1; i < daysInMonth; i++) {
          actualSpending[i] = todayTotal;
        }
      }
      
      monthlySpentTotal = runningTotal;
    }

    // Calculate remaining amount
    const remainingAmount = monthlyTarget - monthlySpentTotal;

    // Generate labels for the x-axis (dates with month)
    const labels = Array.from({ length: daysInMonth }, (_, i) => {
      const date = new Date(today.getFullYear(), today.getMonth(), i + 1);
      return format(date, 'MMM d');
    });

    return {
      target: targetSpending,
      actual: actualSpending,
      labels,
      monthlySpentTotal,
      remainingAmount
    };
  };
  
  return (
    <Box sx={{ maxWidth: { xs: '100%', sm: "390px" }, mx: "auto", pt: 2, pb: 10 }}>
      {/* Add logo */}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {/* Three summary boxes at the top - now fixed */}
      <Box 
        sx={{ 
          position: 'fixed',
          top: 56, // AppBar height
          left: 0,
          right: 0,
          zIndex: 1100,
          pt: 2,
          pb: 2,
          background: '#1A1F35', // Exact match with theme.palette.background.default
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
        }}
      >
        <Box sx={{ maxWidth: { xs: '100%', sm: "390px" }, mx: "auto" }}>
          <Box sx={{ mx: { xs: -1, sm: -2 } }}>
            <Box sx={{ px: { xs: 3, sm: 3 }, display: 'flex', gap: '16px' }}>
              {([
                { 
                  label: 'Day', 
                  loading: loading && !dailyBudget,
                  value: dailyBudget?.spentToday || 0, // Default to 0
                  // Display calculated adjusted daily goal which will be near-zero when over budget
                  remaining: dailyBudget ? calculateAdjustedDailyGoal(dailyBudget) - dailyBudget.spentToday : 0,
                  onClick: () => {
                    // Fetch today's transactions
                    const fetchDailyTransactions = async () => {
                      try {
                        const today = new Date();
                        const start = startOfDay(today);
                        const end = endOfDay(today);
                        const response = await axiosInstance.get('/transactions', {
                          params: {
                            startDate: start.toISOString(),
                            endDate: end.toISOString(),
                            limit: 1000,
                            sortBy: 'date',
                            sortDirection: 'desc'
                          }
                        });
                        setDailyTransactions(response.data.transactions || []);
                        setDailyBreakdownOpen(true);
                      } catch (error) {
                        console.error('Error fetching daily transactions:', error);
                        setDailyTransactions([]);
                      }
                    };
                    fetchDailyTransactions();
                  }
                },
                { 
                  label: 'Week', 
                  loading: weeklyLoading,
                  value: currentWeekTotal || Math.round(dailyBudget?.totalMonthlySpending / 4) || mockData.weekly.spent, 
                  // Calculate remaining as monthly budget remaining divided by weeks remaining in month
                  remaining: dailyBudget ? (() => {
                    const { monthlyLimit, totalMonthlySpending, remainingDays } = dailyBudget;
                    if (!monthlyLimit) return 0;
                    
                    // Calculate remaining budget for the month
                    const remainingBudget = monthlyLimit - totalMonthlySpending;
                    
                    // Calculate weeks remaining in month (approximately)
                    const weeksRemaining = Math.max(1, Math.ceil(remainingDays / 7));
                    
                    // Weekly remaining is the remaining monthly budget divided by weeks remaining
                    return remainingBudget / weeksRemaining;
                  })() : mockData.weekly.remaining,
                  onClick: () => handleWeekClick(3)
                },
                { 
                  label: 'Month', 
                  loading: monthlyLoading,
                  value: dailyBudget?.totalMonthlySpending || 0, // Default to 0 instead of mockData
                  remaining: dailyBudget?.monthlyLimit 
                    ? (dailyBudget.totalMonthlySpending > dailyBudget.monthlyLimit
                      ? -1 * (dailyBudget.totalMonthlySpending - dailyBudget.monthlyLimit) // Show negative when over budget
                      : dailyBudget.monthlyLimit - dailyBudget.totalMonthlySpending) 
                    : mockData.monthly.remaining,
                  onClick: () => {
                    // Fetch this month's transactions
                    const fetchMonthlyTransactions = async () => {
                      try {
                        const today = new Date();
                        const start = startOfMonth(today);
                        const end = endOfMonth(today);
                        const response = await axiosInstance.get('/transactions', {
                          params: {
                            startDate: start.toISOString(),
                            endDate: end.toISOString(),
                            limit: 1000,
                            sortBy: 'date',
                            sortDirection: 'desc'
                          }
                        });
                        setMonthlyTransactions(response.data.transactions || []);
                        setMonthlyBreakdownOpen(true);
                      } catch (error) {
                        console.error('Error fetching monthly transactions:', error);
                        setMonthlyTransactions([]);
                      }
                    };
                    fetchMonthlyTransactions();
                  }
                }
              ] || []).map((item) => (
                <Box key={item.label} sx={{ flex: 1 }}>
                  <Paper 
                    sx={{ 
                      height: 'auto',
                      minHeight: '110px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      borderRadius: '24px',
                      bgcolor: 'rgba(126, 139, 145, 0.2)',
                      backdropFilter: 'blur(8px)',
                      padding: '8px 0',
                      cursor: item.onClick ? 'pointer' : 'default',
                      '&:hover': item.onClick ? {
                        bgcolor: 'rgba(126, 139, 145, 0.3)',
                      } : {}
                    }}
                    onClick={item.onClick}
                  >
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        opacity: 0.7, 
                        fontWeight: 100, 
                        fontSize: '1.25rem',
                        textAlign: 'center'
                      }}
                    >
                      {item.label}
                    </Typography>
                    {item.loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                        <CircularProgress size={20} />
                      </Box>
                    ) : (
                      <>
                        <Typography 
                          sx={{ 
                            fontWeight: 900, 
                            fontSize: '1.5rem', 
                            lineHeight: 1,
                            textAlign: 'center'
                          }}
                        >
                          {formatNumber(item.value)}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            opacity: 0.75, 
                            fontSize: '0.75rem',
                            textAlign: 'center'
                          }}
                        >
                          {formatNumber(item.remaining)}
                        </Typography>
                      </>
                    )}
                  </Paper>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Empty space to push content below fixed header and boxes */}
      <Box sx={{ height: 'calc(110px + 16px + 16px)' }} /> {/* Box height + top padding + bottom padding */}

      {/* Swipeable Charts Container */}
      <Box 
        sx={{ 
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          mt: 2
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <Box sx={{ 
          display: 'flex',
          transition: 'transform 0.3s ease-in-out',
          transform: `translateX(-${activeStep * 100}%)`,
          width: '100%'
        }}>
          {/* Daily Spending Chart */}
          <Box sx={{ width: '100%', flexShrink: 0 }}>
            {showSpendingCard && (
              <Box sx={{ mx: -2 }}>
                <Box sx={{ px: 3 }}>
                  <Paper
                    sx={{
                      p: 3,
                      borderRadius: '24px',
                      mb: 2,
                      position: 'relative',
                      bgcolor: 'rgba(126, 139, 145, 0.2)',
                      backdropFilter: 'blur(8px)',
                      height: isDailyCompact ? '200px' : '260px',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 20 }} ref={menuRef}>
                      <IconButton 
                        onClick={() => setMenuOpen(!menuOpen)}
                        sx={{ 
                          p: 1, 
                          borderRadius: "50%", 
                          bgcolor: "rgba(255, 255, 255, 0.2)",
                          '&:hover': {
                            bgcolor: "rgba(255, 255, 255, 0.3)"
                          }
                        }}
                      >
                        <MoreVertIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      
                      {menuOpen && (
                        <Paper
                          sx={{
                            position: 'absolute',
                            right: 0,
                            mt: 0.5,
                            bgcolor: '#2A3042',
                            boxShadow: 4,
                            borderRadius: 1,
                            overflow: 'hidden',
                            width: 160
                          }}
                        >
                          <Box component="ul" sx={{ py: 1, px: 0, m: 0 }}>
                            <MenuItem onClick={() => {
                              setIsDailyCompact(!isDailyCompact);
                              setMenuOpen(false);
                            }} dense>
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: 16, height: 16, marginRight: 8 }}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                              </svg>
                              {isDailyCompact ? 'Expand' : 'Compact'} View
                            </MenuItem>
                          </Box>
                        </Paper>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ opacity: 0.7, fontWeight: 100 }}>
                        Daily Spending
                      </Typography>
                    </Box>
                    
                    {/* Bar chart container */}
                    <Box sx={{ 
                      flex: 1,
                      position: 'relative',
                      minHeight: 0,
                      mb: 2
                    }}>
                      {/* Target line */}
                      <Box 
                        sx={{ 
                          borderTop: '2px dashed rgba(255, 255, 255, 0.3)',
                          width: '100%',
                          position: 'absolute',
                          zIndex: 10,
                          bottom: `${(dailyTarget / chartMaxValue) * 100}%`
                        }}
                      >
                        <Box sx={{ 
                          position: 'absolute', 
                          top: -20, 
                          right: 0, 
                          fontSize: '0.75rem',
                          color: 'rgba(255, 255, 255, 0.5)',
                          fontWeight: 100
                        }}>
                          Original daily target: {formatNumber(dailyTarget)}
                        </Box>
                      </Box>
                      
                      <Box sx={{ 
                        display: 'flex', 
                        height: '100%', 
                        width: '100%',
                        position: 'absolute',
                        bottom: 0,
                        left: 0
                      }}>
                        {historicalData.amounts.map((amount, i) => {
                          // Calculate how much over budget the amount is
                          const overBudgetRatio = amount > dailyTarget ? 
                            Math.min((amount - dailyTarget) / dailyTarget, 1) : 0;
                          
                          // Create gradient based on budget status
                          let backgroundGradient;
                          if (amount <= dailyTarget) {
                            backgroundGradient = 'linear-gradient(to top, #A6C8D9, #9FDBC1)';
                          } else {
                            backgroundGradient = `linear-gradient(to top, 
                              #A6C8D9 0%, 
                              #9FDBC1 ${(1 - overBudgetRatio) * 100}%, 
                              #FCA5A5 ${(1 - overBudgetRatio) * 100 + 10}%, 
                              #F9A8D4 100%
                            )`;
                          }
                          
                          return (
                            <Box 
                              key={i} 
                              sx={{ 
                                flex: 1, 
                                display: 'flex', 
                                alignItems: 'flex-end',
                                px: '3px',
                                position: 'relative'
                              }}
                            >
                              {/* Show amount above bar when bar is too small */}
                              {((amount / chartMaxValue) * 100) <= 15 && (
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    fontWeight: 500,
                                    position: 'absolute',
                                    bottom: `calc(${(amount / chartMaxValue) * 100}% + 6px)`,
                                    left: 0,
                                    right: 0,
                                    textAlign: 'center',
                                    color: 'rgba(255, 255, 255, 0.75)',
                                    fontSize: '0.7rem',
                                    whiteSpace: 'nowrap',
                                    textShadow: '0px 0px 2px rgba(0, 0, 0, 0.5)',
                                    pointerEvents: 'none',
                                    zIndex: 5
                                  }}
                                >
                                  {formatNumber(amount)}
                                </Typography>
                              )}
                              <Box 
                                onClick={() => handleDailyBarClick(subDays(today, 6 - i))}
                                sx={{ 
                                  height: `${(amount / chartMaxValue) * 100}%`,
                                  width: '100%',
                                  background: backgroundGradient,
                                  borderTopLeftRadius: 4,
                                  borderTopRightRadius: 4,
                                  '&:hover': { opacity: 0.9 },
                                  transition: 'opacity 0.2s',
                                  position: 'relative',
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  justifyContent: 'center'
                                }}
                              >
                                {/* Display amount conditionally based on bar height */}
                                {((amount / chartMaxValue) * 100) > 15 ? (
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      fontWeight: 500, 
                                      pt: 0.5, 
                                      color: 'rgba(55, 65, 81, 0.7)',
                                      fontSize: '0.75rem'
                                    }}
                                  >
                                    {formatNumber(amount)}
                                  </Typography>
                                ) : null}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      mt: 1.5,
                      opacity: 0.7,
                      fontSize: '0.65rem'
                    }}>
                      {historicalData.labels.map((day, i) => (
                        <Box key={i}>{day}</Box>
                      ))}
                    </Box>
                  </Paper>
                </Box>
              </Box>
            )}
          </Box>

          {/* Weekly Spending Chart */}
          <Box sx={{ width: '100%', flexShrink: 0 }}>
            {showWeeklyCard && (
              <Box sx={{ mx: -2 }}>
                <Box sx={{ px: 3 }}>
                  <Paper
                    sx={{
                      p: 3,
                      borderRadius: '24px',
                      mb: 2,
                      position: 'relative',
                      bgcolor: 'rgba(126, 139, 145, 0.2)',
                      backdropFilter: 'blur(8px)',
                      height: isWeeklyCompact ? '200px' : '260px',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 20 }} ref={weeklyMenuRef}>
                      <IconButton 
                        onClick={() => setWeeklyMenuOpen(!weeklyMenuOpen)}
                        sx={{ 
                          p: 1, 
                          borderRadius: "50%", 
                          bgcolor: "rgba(255, 255, 255, 0.2)",
                          '&:hover': {
                            bgcolor: "rgba(255, 255, 255, 0.3)"
                          }
                        }}
                      >
                        <MoreVertIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      
                      {weeklyMenuOpen && (
                        <Paper
                          sx={{
                            position: 'absolute',
                            right: 0,
                            mt: 0.5,
                            bgcolor: '#2A3042',
                            boxShadow: 4,
                            borderRadius: 1,
                            overflow: 'hidden',
                            width: 160
                          }}
                        >
                          <Box component="ul" sx={{ py: 1, px: 0, m: 0 }}>
                            <MenuItem onClick={() => {
                              setIsWeeklyCompact(!isWeeklyCompact);
                              setWeeklyMenuOpen(false);
                            }} dense>
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: 16, height: 16, marginRight: 8 }}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                              </svg>
                              {isWeeklyCompact ? 'Expand' : 'Compact'} View
                            </MenuItem>
                          </Box>
                        </Paper>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ opacity: 0.7, fontWeight: 100 }}>
                        Weekly Spending
                      </Typography>
                    </Box>
                    
                    {/* Bar chart container */}
                    <Box sx={{ 
                      flex: 1,
                      position: 'relative',
                      minHeight: 0,
                      mb: 2
                    }}>
                      {/* Target line - use originalWeeklyTarget for display */}
                      <Box 
                        sx={{ 
                          borderTop: '2px dashed rgba(255, 255, 255, 0.3)',
                          width: '100%',
                          position: 'absolute',
                          zIndex: 10,
                          bottom: `${(originalWeeklyTarget / weeklyChartMaxValue) * 100}%`
                        }}
                      >
                        <Box sx={{ 
                          position: 'absolute', 
                          top: -20, 
                          right: 0, 
                          fontSize: '0.75rem',
                          color: 'rgba(255, 255, 255, 0.5)',
                          fontWeight: 100
                        }}>
                          Original weekly target: {formatNumber(originalWeeklyTarget)}
                        </Box>
                      </Box>
                      
                      <Box sx={{ 
                        display: 'flex', 
                        height: '100%', 
                        width: '100%',
                        position: 'absolute',
                        bottom: 0,
                        left: 0
                      }}>
                        {weeklyLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                          </Box>
                        ) : (
                          weeklyData.amounts.map((amount, i) => {
                            // Calculate how much over budget the amount is
                            const overBudgetRatio = amount > originalWeeklyTarget ? 
                              Math.min((amount - originalWeeklyTarget) / originalWeeklyTarget, 1) : 0;
                            
                            // Create gradient based on budget status
                            let backgroundGradient;
                            if (amount <= originalWeeklyTarget) {
                              backgroundGradient = 'linear-gradient(to top, #A6C8D9, #9FDBC1)';
                            } else {
                              backgroundGradient = `linear-gradient(to top, 
                                #A6C8D9 0%, 
                                #9FDBC1 ${(1 - overBudgetRatio) * 100}%, 
                                #FCA5A5 ${(1 - overBudgetRatio) * 100 + 10}%, 
                                #F9A8D4 100%
                              )`;
                            }
                            
                            return (
                              <Box 
                                key={i} 
                                sx={{ 
                                  flex: 1, 
                                  display: 'flex', 
                                  alignItems: 'flex-end',
                                  px: '3px',
                                  position: 'relative'
                                }}
                              >
                                {/* Show amount above bar when bar is too small */}
                                {((amount / weeklyChartMaxValue) * 100) <= 15 && (
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      fontWeight: 500,
                                      position: 'absolute',
                                      bottom: `calc(${(amount / weeklyChartMaxValue) * 100}% + 6px)`,
                                      left: 0,
                                      right: 0,
                                      textAlign: 'center',
                                      color: 'rgba(255, 255, 255, 0.75)',
                                      fontSize: '0.7rem',
                                      whiteSpace: 'nowrap',
                                      textShadow: '0px 0px 2px rgba(0, 0, 0, 0.5)',
                                      pointerEvents: 'none',
                                      zIndex: 5
                                    }}
                                  >
                                    {formatNumber(amount)}
                                  </Typography>
                                )}
                                <Box 
                                  onClick={() => handleWeekClick(i)}
                                  sx={{ 
                                    height: `${(amount / weeklyChartMaxValue) * 100}%`, 
                                    width: '100%',
                                    background: backgroundGradient,
                                    borderTopLeftRadius: 4,
                                    borderTopRightRadius: 4,
                                    '&:hover': { 
                                      opacity: 0.9,
                                      cursor: 'pointer'
                                    },
                                    transition: 'opacity 0.2s',
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'center'
                                  }}
                                >
                                  {/* Display amount conditionally based on bar height */}
                                  {((amount / weeklyChartMaxValue) * 100) > 15 ? (
                                    <Typography 
                                      variant="caption" 
                                      sx={{ 
                                        fontWeight: 500, 
                                        pt: 0.5, 
                                        color: 'rgba(55, 65, 81, 0.7)',
                                        fontSize: '0.75rem'
                                      }}
                                    >
                                      {formatNumber(amount)}
                                    </Typography>
                                  ) : null}
                                </Box>
                              </Box>
                            );
                          })
                        )}
                      </Box>
                    </Box>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      mt: 1.5,
                      opacity: 0.7,
                      fontSize: '0.65rem'
                    }}>
                      {weeklyData.labels.map((week, i) => (
                        <Box key={i} sx={{ width: '25%', textAlign: 'center' }}>{week}</Box>
                      ))}
                    </Box>
                  </Paper>
                </Box>
              </Box>
            )}
          </Box>

          {/* Monthly Spending Chart */}
          <Box sx={{ width: '100%', flexShrink: 0 }}>
            {showMonthlyCard && (
              <Box sx={{ mx: -2 }}>
                <Box sx={{ px: 3 }}>
                  <Paper
                    sx={{
                      p: 3,
                      borderRadius: '24px',
                      mb: 2,
                      position: 'relative',
                      bgcolor: 'rgba(126, 139, 145, 0.2)',
                      backdropFilter: 'blur(8px)',
                      height: isMonthlyCompact ? '200px' : '260px',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 20 }} ref={monthlyMenuRef}>
                      <IconButton 
                        onClick={() => setMonthlyMenuOpen(!monthlyMenuOpen)}
                        sx={{ 
                          p: 1, 
                          borderRadius: "50%", 
                          bgcolor: "rgba(255, 255, 255, 0.2)",
                          '&:hover': {
                            bgcolor: "rgba(255, 255, 255, 0.3)"
                          }
                        }}
                      >
                        <MoreVertIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      
                      {monthlyMenuOpen && (
                        <Paper
                          sx={{
                            position: 'absolute',
                            right: 0,
                            mt: 0.5,
                            bgcolor: '#2A3042',
                            boxShadow: 4,
                            borderRadius: 1,
                            overflow: 'hidden',
                            width: 160
                          }}
                        >
                          <Box component="ul" sx={{ py: 1, px: 0, m: 0 }}>
                            <MenuItem onClick={() => {
                              setIsMonthlyCompact(!isMonthlyCompact);
                              setMonthlyMenuOpen(false);
                            }} dense>
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: 16, height: 16, marginRight: 8 }}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                              </svg>
                              {isMonthlyCompact ? 'Expand' : 'Compact'} View
                            </MenuItem>
                          </Box>
                        </Paper>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ opacity: 0.7, fontWeight: 100 }}>
                        Monthly Spending
                      </Typography>
                    </Box>
                    
                    {/* Bar chart container */}
                    <Box sx={{ 
                      flex: 1,
                      position: 'relative',
                      minHeight: 0,
                      mb: 2
                    }}>
                      {/* Target line */}
                      <Box 
                        sx={{ 
                          borderTop: '2px dashed rgba(255, 255, 255, 0.3)',
                          width: '100%',
                          position: 'absolute',
                          zIndex: 10,
                          bottom: `${(monthlyTarget / monthlyChartMaxValue) * 100}%`
                        }}
                      >
                        <Box sx={{ 
                          position: 'absolute', 
                          top: -20, 
                          right: 0, 
                          fontSize: '0.75rem',
                          color: 'rgba(255, 255, 255, 0.5)',
                          fontWeight: 100
                        }}>
                          Monthly target: {formatNumber(monthlyTarget)}
                        </Box>
                      </Box>
                      
                      <Box sx={{ 
                        display: 'flex', 
                        height: '100%', 
                        width: '100%',
                        position: 'absolute',
                        bottom: 0,
                        left: 0
                      }}>
                        {monthlyLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', alignItems: 'center' }}>
                            <CircularProgress size={30} />
                          </Box>
                        ) : (
                          monthlyData.amounts.map((amount, i) => {
                            // Calculate how much over budget the amount is
                            const overBudgetRatio = amount > monthlyTarget ? 
                              Math.min((amount - monthlyTarget) / monthlyTarget, 1) : 0;
                            
                            // Create gradient based on budget status
                            let backgroundGradient;
                            if (amount <= monthlyTarget) {
                              backgroundGradient = 'linear-gradient(to top, #A6C8D9, #9FDBC1)';
                            } else {
                              backgroundGradient = `linear-gradient(to top, 
                                #A6C8D9 0%, 
                                #9FDBC1 ${(1 - overBudgetRatio) * 100}%, 
                                #FCA5A5 ${(1 - overBudgetRatio) * 100 + 10}%, 
                                #F9A8D4 100%
                              )`;
                            }
                            
                            return (
                              <Box 
                                key={i} 
                                sx={{ 
                                  flex: 1, 
                                  display: 'flex', 
                                  alignItems: 'flex-end',
                                  px: '3px',
                                  position: 'relative'
                                }}
                              >
                                {/* Show amount above bar when bar is too small */}
                                {((amount / monthlyChartMaxValue) * 100) <= 15 && (
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      fontWeight: 500,
                                      position: 'absolute',
                                      bottom: `calc(${(amount / monthlyChartMaxValue) * 100}% + 6px)`,
                                      left: 0,
                                      right: 0,
                                      textAlign: 'center',
                                      color: 'rgba(255, 255, 255, 0.75)',
                                      fontSize: '0.7rem',
                                      whiteSpace: 'nowrap',
                                      textShadow: '0px 0px 2px rgba(0, 0, 0, 0.5)',
                                      pointerEvents: 'none',
                                      zIndex: 5
                                    }}
                                  >
                                    {formatNumber(amount)}
                                  </Typography>
                                )}
                                <Box 
                                  onClick={() => handleMonthlyBarClick(i)}
                                  sx={{ 
                                    height: `${(amount / monthlyChartMaxValue) * 100}%`, 
                                    width: '100%',
                                    background: backgroundGradient,
                                    borderTopLeftRadius: 4,
                                    borderTopRightRadius: 4,
                                    '&:hover': { 
                                      opacity: 0.9,
                                      cursor: 'pointer'
                                    },
                                    transition: 'opacity 0.2s',
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'center'
                                  }}
                                >
                                  {/* Display amount conditionally based on bar height */}
                                  {((amount / monthlyChartMaxValue) * 100) > 15 ? (
                                    <Typography 
                                      variant="caption" 
                                      sx={{ 
                                        fontWeight: 500, 
                                        pt: 0.5, 
                                        color: 'rgba(55, 65, 81, 0.7)',
                                        fontSize: '0.75rem'
                                      }}
                                    >
                                      {formatNumber(amount)}
                                    </Typography>
                                  ) : null}
                                </Box>
                              </Box>
                            );
                          })
                        )}
                      </Box>
                    </Box>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      mt: 1.5,
                      opacity: 0.7,
                      fontSize: '0.65rem'
                    }}>
                      {monthlyData.labels.map((month, i) => (
                        <Box key={i} sx={{ width: '33%', textAlign: 'center' }}>{month}</Box>
                      ))}
                    </Box>
                  </Paper>
                </Box>
              </Box>
            )}
          </Box>

          {/* Progress Line Chart */}
          <Box sx={{ width: '100%', flexShrink: 0 }}>
            <Box sx={{ mx: -2 }}>
              <Box sx={{ px: 3 }}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: '24px',
                    mb: 2,
                    position: 'relative',
                    bgcolor: 'rgba(126, 139, 145, 0.2)',
                    backdropFilter: 'blur(8px)',
                    height: '260px',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Typography variant="h6" sx={{ opacity: 0.7, fontWeight: 100, mb: 2 }}>
                    Monthly Progress
                  </Typography>
                  
                  {/* Line chart container */}
                  <Box sx={{ 
                    flex: 1,
                    position: 'relative',
                    minHeight: 0,
                    mb: 2
                  }}>
                    {(() => {
                      const data = getCumulativeSpendingData();
                      const chartHeight = 160;
                      const maxValue = Math.max(...data.target, ...data.actual);
                      const scaleFactor = chartHeight / (maxValue * 1.5);

                      // Create path commands for both lines
                      const targetPoints = data.target.map((value, index) => ({
                        x: (index / (data.target.length - 1)) * 100,
                        y: chartHeight - (value * scaleFactor)
                      }));

                      const actualPoints = data.actual.map((value, index) => ({
                        x: (index / (data.actual.length - 1)) * 100,
                        y: chartHeight - (value * scaleFactor)
                      }));

                      // Create fill paths for areas above and below target
                      const fillPaths = [];
                      for (let i = 0; i < actualPoints.length - 1; i++) {
                        const path = `
                          M ${actualPoints[i].x} ${actualPoints[i].y}
                          L ${actualPoints[i + 1].x} ${actualPoints[i + 1].y}
                          L ${targetPoints[i + 1].x} ${targetPoints[i + 1].y}
                          L ${targetPoints[i].x} ${targetPoints[i].y}
                          Z
                        `;

                        const isAboveTarget = actualPoints[i].y < targetPoints[i].y;
                        fillPaths.push({
                          path,
                          isAbove: isAboveTarget
                        });
                      }

                      // Create the actual line path
                      const actualLine = `M ${actualPoints[0].x} ${actualPoints[0].y} ` + 
                        actualPoints.slice(1).map(point => `L ${point.x} ${point.y}`).join(' ');

                      return (
                        <>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              position: 'absolute',
                              top: 20,
                              right: 0,
                              color: 'rgba(255, 255, 255, 0.75)',
                              fontSize: '0.75rem',
                              fontFamily: 'Barlow',
                              fontWeight: 'medium',
                              display: 'flex',
                              gap: '8px'
                            }}
                          >
                            <span>Spent: {formatNumber(data.monthlySpentTotal)}</span>
                            <span style={{ 
                              color: data.remainingAmount < 0 ? 'rgba(252, 165, 165, 0.75)' : 'rgba(159, 219, 193, 0.75)'
                            }}>
                              {data.remainingAmount < 0 ? 'Overspent: ' : 'Remaining: '}
                              {formatNumber(Math.abs(data.remainingAmount))}
                            </span>
                          </Typography>

                          <svg
                            width="100%"
                            height={chartHeight}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              overflow: 'visible'
                            }}
                            viewBox={`0 0 100 ${chartHeight}`}
                            preserveAspectRatio="none"
                          >
                            {/* Fill areas between lines */}
                            {fillPaths.map((fill, index) => (
                              <path
                                key={index}
                                d={fill.path}
                                fill={fill.isAbove ? 'rgba(252, 165, 165, 0.2)' : 'rgba(159, 219, 193, 0.2)'}
                                stroke="none"
                              />
                            ))}
                            
                            {/* Draw target line */}
                            <path
                              d={`M ${targetPoints[0].x} ${targetPoints[0].y} ` + 
                                targetPoints.slice(1).map(point => `L ${point.x} ${point.y}`).join(' ')}
                              stroke="rgba(255, 255, 255, 0.3)"
                              strokeWidth="2"
                              strokeDasharray="4 4"
                              fill="none"
                              vectorEffect="non-scaling-stroke"
                            />
                            
                            {/* Draw actual spending line */}
                            <path
                              d={actualLine}
                              stroke="#9FDBC1"
                              strokeWidth="2"
                              fill="none"
                              vectorEffect="non-scaling-stroke"
                            />

                            {/* Add current position dot at today's position */}
                            <circle
                              cx={actualPoints[today.getDate() - 1].x}
                              cy={actualPoints[today.getDate() - 1].y}
                              r="2.5"
                              fill="#9FDBC1"
                              stroke="#1A1F35"
                              strokeWidth="1"
                              vectorEffect="non-scaling-stroke"
                              style={{
                                transformBox: 'fill-box',
                                transformOrigin: 'center center',
                                transform: 'scale(0.3, 1)'
                              }}
                            />
                          </svg>
                        </>
                      );
                    })()}
                  </Box>
                  
                  {/* X-axis labels */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    mt: 1.5,
                    opacity: 0.7,
                    fontSize: '0.65rem'
                  }}>
                    {getCumulativeSpendingData().labels.filter((_, i) => i % 5 === 0).map((label, i) => (
                      <Box key={i}>{label}</Box>
                    ))}
                  </Box>
                </Paper>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Indicator Dots */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          mt: 2,
          mb: 2
        }}>
          {[...Array(maxSteps)].map((_, index) => (
            <Box
              key={index}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                mx: 0.5,
                bgcolor: activeStep === index ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.2)',
                transition: 'background-color 0.3s ease-in-out'
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Keep the original Daily Spending Card and Monthly Overview Card for functionality */}
      <Box sx={{ display: 'none' }}>
      {/* Daily Spending Card */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          borderRadius: 2,
            mb: 2,
        }}
      >
          <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
          </Typography>
          
          {loading && !dailyBudget ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : !dailyBudget ? (
            <Alert severity="info">
              Please set your monthly spending limit in the Settings page.
            </Alert>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                    <Typography variant="h6" color="customBlue.main" fontWeight="bold" >
                    Daily Goal
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="h6" fontWeight="medium" textAlign="right"
                    >
                    <Box component="span" color="customBlue.main" fontWeight="bold">
                      {formatCurrency(calculateAdjustedDailyGoal(dailyBudget))}
                    </Box>
                  </Typography>
                </Grid>
              </Grid>
              
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ position: 'relative', height: '24px', mb: 1 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={calculateProgress()} 
                  color={getProgressColor()}
                  sx={{ 
                        height: 24,
                        borderRadius: 2,
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)', // Basic shadow
                        '& .MuiLinearProgress-bar': {
                          transition: 'transform 0.4s ease-in-out',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24), 0 0 8px rgba(0,0,0,0.1)'
                        }
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 1
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="white"
                        sx={{
                          fontWeight: 'medium',
                          fontSize: '0.7rem',  // Explicit font size (12px)
                          textShadow: '1px 2px 2px rgba(0,0,0,0.5)',
                        }}
                      >
                        {formatCurrency(dailyBudget.spentToday)}
                      </Typography>
                      
                      <Typography
                        variant="caption"
                        color="white"
                        sx={{
                          fontWeight: 'bold',
                          
                          textShadow: '1px 2px 2px rgba(0,0,0,0.5)',
                        }}
                      >
                        {Math.min(100, Math.round((dailyBudget.spentToday / calculateAdjustedDailyGoal(dailyBudget)) * 100 || 0))}%
                </Typography>
                      
                <Typography 
                        variant="body2"
                        color="white"
                        sx={{
                          fontWeight: 'medium',
                          fontSize: '0.7rem',  // Explicit font size (12px)
                          textShadow: '1px 2px 2px rgba(0,0,0,0.5)',
                        }}
                      >
                        {formatCurrency(Math.max(0, calculateAdjustedDailyGoal(dailyBudget) - dailyBudget.spentToday))}
                </Typography>
                    </Box>
                  </Box>
              </Box>
            </>
          )}
        </Box>
      </Paper>
      
      {/* Monthly Overview Card */}
      {dailyBudget && (
        <Paper
          elevation={2}
          sx={{
            p: 3,
            borderRadius: 2,
          }}
        >
          
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
                  <Typography variant="h6" fontWeight="medium" color="text.secondary">
                    Monthly Goal
              </Typography>
            </Grid>
            <Grid item xs={6}>
                  <Typography variant="h6" fontWeight="medium" color="text.secondary" textAlign="right">
                    {formatCurrency(dailyBudget.monthlyLimit)}
              </Typography>
            </Grid>
          </Grid>
          
              <Box sx={{ mb: 2 }}>
                <Box sx={{ position: 'relative', height: '24px', mb: 1 }}>
            <LinearProgress 
              variant="determinate" 
                    value={calculateMonthlyProgress()} 
                    color={getMonthlyProgressColor()}
              sx={{ 
                      height: 24, 
                      borderRadius: 2,
                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                      '& .MuiLinearProgress-bar': {
                        transition: 'transform 0.4s ease-in-out'
                      },
                      backgroundColor: ({ palette }) => {
                        // Match background color to a lighter version of the progress color
                        const color = getMonthlyProgressColor();
                        return `${palette[color].light}40`; // 40 = 25% opacity
                      }
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 2
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="white"
                      sx={{
                        fontWeight: 'medium',
                        textShadow: '1px 2px 2px rgba(0,0,0,0.5)',
                        fontSize: '0.7rem',  // Explicit font size (12px)
                      }}
                    >
                      {formatCurrency(dailyBudget.totalMonthlySpending)}
                    </Typography>
                    
                    <Typography
                      variant="caption"
                      color="white"
                      sx={{
                        fontWeight: 'bold',
                        textShadow: '1px 2px 2px rgba(0,0,0,0.5)',
                      }}
                    >
                      {Math.min(100, Math.round(calculateMonthlyProgress()))}%
                    </Typography>
                  </Box>
                </Box>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  $ Remaining in {dailyBudget.month}
            </Typography>
            <Typography 
                    variant="caption" 
              fontWeight="medium"
                    color={calculateRemainingMonthlySpending(dailyBudget) > 0 ? 'success.main' : 'error.main'}
            >
                    {formatCurrency(calculateRemainingMonthlySpending(dailyBudget))}
            </Typography>
          </Box>
          
            <Box sx={{ mt: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
              Days Remaining
            </Typography>
              <Typography variant="caption" fontWeight="light">
              {dailyBudget.remainingDays} of {dailyBudget.daysInMonth}
            </Typography>
          </Box>
        </Paper>
      )}
      </Box>
      
      {/* Floating Action Button for adding purchases */}
        <Fab
          color="primary"
        aria-label="add"
        onClick={handleOpenDialog}
          sx={{
            position: 'fixed',
          bottom: 90, // Raised higher from bottom (was 80)
          right: 24,
          width: 56,
          height: 56,
          zIndex: 9999,
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.3)',
          background: 'linear-gradient(135deg, #A6C8D9, #9FDBC1)',
          '&:hover': {
            background: 'linear-gradient(135deg, #9FDBC1, #A6C8D9)',
            transform: 'translateY(-2px)',
            boxShadow: '0px 6px 10px rgba(0, 0, 0, 0.4)',
          },
          transition: 'all 0.3s ease',
        }}
      >
        <AddIcon fontSize="large" />
        </Fab>
      
      {/* Log Purchase Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        fullWidth 
        maxWidth="sm"
        PaperProps={{
          style: {
            background: 'rgba(31, 41, 55, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }
        }}
      >
        <DialogTitle>Log Purchase</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {formError}
            </Alert>
          )}
          
          {/* Numeric Keypad */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h5" align="right" sx={{ mb: 6, fontWeight: 'bold' }}>
              ${numericInput || '0'}
            </Typography>
            
            <Grid container spacing={1} sx={{ mb: 1 }}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                <Grid item xs={4} key={num}>
                  <Button 
                    variant="outlined" 
            fullWidth
                    onClick={() => handleNumericInput(num.toString())}
                    sx={{ 
                      height: { xs: 40, sm: 56 },
                      borderRadius: 2,
                      minWidth: 'unset',
                      padding: { xs: '4px', sm: '8px 16px' }
                    }}
                  >
                    {num}
                  </Button>
                </Grid>
              ))}
              <Grid item xs={4}>
                <Button 
                  variant="outlined" 
                  fullWidth 
                  onClick={() => handleNumericInput('.')}
                  sx={{ 
                    height: { xs: 40, sm: 56 },
                    borderRadius: 2,
                    minWidth: 'unset',
                    padding: { xs: '4px', sm: '8px 16px' }
                  }}
                >
                  .
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Button 
                  variant="outlined" 
                  fullWidth 
                  onClick={() => handleNumericInput('0')}
                  sx={{ 
                    height: { xs: 40, sm: 56 },
                    borderRadius: 2,
                    minWidth: 'unset',
                    padding: { xs: '4px', sm: '8px 16px' }
                  }}
                >
                  0
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Button 
                  variant="outlined" 
                  color="error" 
                  fullWidth 
                  onClick={() => handleNumericInput('backspace')}
                  sx={{ 
                    height: { xs: 40, sm: 56 },
                    borderRadius: 2,
                    minWidth: 'unset',
                    padding: { xs: '4px', sm: '8px 16px' }
                  }}
                >
                  <BackspaceIcon />
                </Button>
              </Grid>
            </Grid>
          </Box>
          
          {/* Quick Categories */}
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Quick Categories:
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {quickCategories.map(cat => (
              <Button 
                key={cat}
                variant={category === cat ? "contained" : "outlined"}
                color="primary"
                onClick={() => handleQuickCategory(cat)}
                disabled={numericInput === ''}
                sx={{ 
                  borderRadius: 2, 
                  height: 36,
                  flex: '1 0 18%', 
                  fontSize: '0.65rem',
                  textTransform: 'none',
                  // For non-selected buttons
                  ...(category !== cat && {
                    bgcolor: 'rgba(159, 219, 193, 0.1)',
                    border: '1px solid',
                    borderColor: '#9FDBC1',
                    color: '#9FDBC1'
                  }),
                  
                  // For selected buttons
                  ...(category === cat && {
                    bgcolor: '#9FDBC1',
                    color: '#1A1F35',
                  }),
                  minWidth: 'unset',
                  padding: '4px 8px'
                }}
              >
                {cat}
              </Button>
            ))}
          </Box>
          
          {/* Category Dropdown */}
          <FormControl fullWidth margin="dense" sx={{ mb: 1.5 }} size="small">
            <InputLabel id="category-label" sx={{ 
              fontSize: '0.85rem',
              color: 'rgba(255, 255, 255, 0.7)',
            }}>Category</InputLabel>
            <Select
              labelId="category-label"
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                if (e.target.value !== 'custom') {
                  setCustomCategory('');
                }
              }}
              label="Category"
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#9FDBC1',
                },
                color: 'white',
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    background: 'rgba(31, 41, 55, 0.95)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                  },
                },
              }}
            >
              <MenuItem value="">
                <em>Uncategorized</em>
              </MenuItem>
              {CATEGORIES.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
              <MenuItem value="custom">Custom Category</MenuItem>
            </Select>
          </FormControl>
          
          {/* Custom Category */}
          {category === 'custom' && (
            <TextField
              margin="dense"
              label="Custom Category Name"
              fullWidth
              required
              value={customCategory}
              onChange={(e) => setCustomCategory(e.target.value)}
              sx={{ 
                mb: 1.5,
                '& .MuiInputLabel-root': { 
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '0.85rem'
                },
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#9FDBC1',
                  },
                },
              }}
            />
          )}
          
          {/* Date Picker */}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Date"
              value={selectedDate}
              onChange={(newDate) => setSelectedDate(newDate)}
              maxDate={new Date()} // Don't allow future dates
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  margin: "dense",
                  sx: {
                    mb: 1.5,
                    '& .MuiInputLabel-root': { 
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.85rem'
                    },
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#9FDBC1',
                      },
                      fontSize: '0.85rem',
                      height: '40px',
                    },
                    '& .MuiOutlinedInput-input': { 
                      fontSize: '0.85rem',
                      padding: '8px 14px',
                      color: 'white',
                    },
                    '& .MuiSvgIcon-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                    }
                  }
                },
                popper: {
                  sx: {
                    '& .MuiPaper-root': {
                      background: 'rgba(31, 41, 55, 0.95)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                    },
                    '& .MuiCalendarPicker-root': { 
                      color: 'white',
                      fontSize: '0.85rem'
                    },
                    '& .MuiPickersDay-root': { 
                      color: 'white',
                      fontSize: '0.85rem'
                    }
                  }
                }
              }}
            />
          </LocalizationProvider>
          
          {/* Note Field */}
          <TextField
            margin="dense"
            label="Note (Optional)"
            fullWidth
            multiline
            rows={2}
            size="small"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            sx={{ 
              mb: 1.5,
              '& .MuiInputLabel-root': { 
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.85rem'
              },
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#9FDBC1',
                },
              },
              '& .MuiInputBase-input': { 
                fontSize: '0.85rem',
                color: 'white',
              }
            }}
          />
        </DialogContent>
        
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button 
            onClick={handleCloseDialog}
            size="small"
            sx={{ 
              fontSize: '0.85rem',
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': {
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            size="small"
            sx={{ 
              fontSize: '0.85rem',
              background: 'linear-gradient(135deg, #A6C8D9, #9FDBC1)',
              color: '#1A1F35',
              '&:hover': {
                background: 'linear-gradient(135deg, #9FDBC1, #A6C8D9)',
              }
            }}
            disabled={loading || (numericInput === '' && amount === '')}
          >
            {loading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Weekly Breakdown Dialog */}
      <Dialog
        open={breakdownOpen}
        onClose={() => setBreakdownOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            bgcolor: 'rgba(26, 31, 53, 0.95)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          color: 'rgba(255, 255, 255, 0.9)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          pb: 2
        }}>
          {weeklyData.labels[selectedWeekIndex]}
        </DialogTitle>
        <DialogContent>
          <Box>
            <Typography variant="h5" sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.9)' }}>
              Total: {formatNumber(calculateTotal(weeklyTransactions[selectedWeekIndex]))}
            </Typography>
            <List>
              {getCategoryBreakdown(weeklyTransactions[selectedWeekIndex] || []).map((item, index) => (
                <React.Fragment key={item.category}>
                  <ListItemButton 
                    onClick={() => handleCategoryClick(item.category)}
                    sx={{ px: 0 }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                            {item.category}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                              {formatNumber(item.amount)}
                            </Typography>
                            {expandedCategories[item.category] ? <ExpandLess /> : <ExpandMore />}
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={parseFloat(item.percentage)}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor: 'rgba(255, 255, 255, 0.1)',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 3,
                                bgcolor: '#9FDBC1'
                              }
                            }}
                          />
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', mt: 0.5, display: 'block' }}>
                            {item.percentage}% of total ({item.count} transactions)
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                  <Collapse in={expandedCategories[item.category]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {getCategoryTransactions(weeklyTransactions[selectedWeekIndex] || [], item.category).map((transaction, tIndex) => (
                        <ListItem 
                          key={tIndex} 
                          sx={{ 
                            pl: 4, 
                            py: 1,
                            borderLeft: '2px solid rgba(159, 219, 193, 0.3)'
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                                {formatNumber(transaction.amount)}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                                {format(new Date(transaction.date), 'EEE, MMM d, h:mm a')}
                                {transaction.note && ` - ${transaction.note}`}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                  {index < weeklyTransactions[selectedWeekIndex].length - 1 && (
                    <Divider sx={{ my: 1, bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                  )}
                </React.Fragment>
              ))}
            </List>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Button 
            onClick={() => setBreakdownOpen(false)}
            sx={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Daily Breakdown Dialog */}
      <Dialog
        open={dailyBreakdownOpen}
        onClose={() => setDailyBreakdownOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            bgcolor: 'rgba(26, 31, 53, 0.95)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          color: 'rgba(255, 255, 255, 0.9)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          pb: 2
        }}>
          {format(new Date(dailyTransactions[0]?.date || new Date()), 'EEEE, MMMM d')}
        </DialogTitle>
        <DialogContent>
          <Box>
            <Typography variant="h5" sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.9)' }}>
              Total: {formatNumber(calculateTotal(dailyTransactions))}
            </Typography>
            <List>
              {getCategoryBreakdown(dailyTransactions || []).map((item, index) => (
                <React.Fragment key={item.category}>
                  <ListItemButton 
                    onClick={() => handleCategoryClick(item.category)}
                    sx={{ px: 0 }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                            {item.category}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                              {formatNumber(item.amount)}
                            </Typography>
                            {expandedCategories[item.category] ? <ExpandLess /> : <ExpandMore />}
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={parseFloat(item.percentage)}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor: 'rgba(255, 255, 255, 0.1)',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 3,
                                bgcolor: '#9FDBC1'
                              }
                            }}
                          />
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', mt: 0.5, display: 'block' }}>
                            {item.percentage}% of total ({item.count} transactions)
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                  <Collapse in={expandedCategories[item.category]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {getCategoryTransactions(dailyTransactions || [], item.category).map((transaction, tIndex) => (
                        <ListItem 
                          key={tIndex} 
                          sx={{ 
                            pl: 4, 
                            py: 1,
                            borderLeft: '2px solid rgba(159, 219, 193, 0.3)'
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                                {formatNumber(transaction.amount)}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                                {format(new Date(transaction.date), 'EEE, h:mm a')}
                                {transaction.note && ` - ${transaction.note}`}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                  {index < dailyTransactions.length - 1 && (
                    <Divider sx={{ my: 1, bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                  )}
                </React.Fragment>
              ))}
            </List>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Button 
            onClick={() => setDailyBreakdownOpen(false)}
            sx={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Monthly Breakdown Dialog */}
      <Dialog
        open={monthlyBreakdownOpen}
        onClose={() => setMonthlyBreakdownOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            bgcolor: 'rgba(26, 31, 53, 0.95)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          color: 'rgba(255, 255, 255, 0.9)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          pb: 2
        }}>
          {format(new Date(monthlyTransactions[0]?.date || new Date()), 'MMMM yyyy')}
        </DialogTitle>
        <DialogContent>
          <Box>
            <Typography variant="h5" sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.9)' }}>
              Total: {formatNumber(calculateTotal(monthlyTransactions))}
            </Typography>
            <List>
              {getCategoryBreakdown(monthlyTransactions || []).map((item, index) => (
                <React.Fragment key={item.category}>
                  <ListItemButton 
                    onClick={() => handleCategoryClick(item.category)}
                    sx={{ px: 0 }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                            {item.category}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                              {formatNumber(item.amount)}
                            </Typography>
                            {expandedCategories[item.category] ? <ExpandLess /> : <ExpandMore />}
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={parseFloat(item.percentage)}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor: 'rgba(255, 255, 255, 0.1)',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 3,
                                bgcolor: '#9FDBC1'
                              }
                            }}
                          />
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', mt: 0.5, display: 'block' }}>
                            {item.percentage}% of total ({item.count} transactions)
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                  <Collapse in={expandedCategories[item.category]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {getCategoryTransactions(monthlyTransactions || [], item.category).map((transaction, tIndex) => (
                        <ListItem 
                          key={tIndex} 
                          sx={{ 
                            pl: 4, 
                            py: 1,
                            borderLeft: '2px solid rgba(159, 219, 193, 0.3)'
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                                {formatNumber(transaction.amount)}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                                {format(new Date(transaction.date), 'EEE, MMM d, h:mm a')}
                                {transaction.note && ` - ${transaction.note}`}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                  {index < monthlyTransactions.length - 1 && (
                    <Divider sx={{ my: 1, bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                  )}
                </React.Fragment>
              ))}
            </List>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Button 
            onClick={() => setMonthlyBreakdownOpen(false)}
            sx={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HomePage;